import { effect, inject, Injectable, OnInit, signal, WritableSignal } from '@angular/core';
import { Pack } from '../interfaces/pack';
import { Seat } from '../interfaces/seat';
import { PackSeatValidation } from '../interfaces/pack-seat-validation';
import { HttpClient } from '@angular/common/http';
import { AppService } from './app.service';
import { ApiResponse } from '../interfaces/api-response';


@Injectable({
	providedIn: 'root'
})
export class PackService implements OnInit {

    private app = inject(AppService)
    private http = inject(HttpClient)
    private storeCheckHash: string = '';
    
    public currentPacks: WritableSignal<Pack[]> = signal([]);
    public userPacks: WritableSignal<Pack[]> = signal([]);


    userPackData = effect(() => {
        const packs = this.userPacks();
        if(packs.length > 0 && this.storeCheckHash !== JSON.stringify(packs)) {
            this.storeUserPacks(packs);
        }
    });


    /**
     * Loads all user packs into signals on initialization.
     */
    ngOnInit() {
        this.loadAllUsersPacks();
    }

    
    /**
     * Loads packs from storage and sets the current packs.
     */
    public loadAllUsersPacks(force: boolean = false) {

        // Load users own packs into the userPacks signal
        // This shouldent be needed - We should be able to find the users own packs in the currentPacks signal, but the current user-id is not available
        // TODO: Fix this
        this.loadUserPacks();

        // Load all users packs into the currentPacks signal
        if(this.currentPacks().length > 0 && !force) {
            return;
        }
        this.http.post(this.app.getApiEndpoint() + '/oauth/get-all-users-packs', {}).subscribe({
            next: (response: ApiResponse) => {
                if(response.results) {
                    let packs = response.results.reduce((allPacks, user) => {
                        return [...allPacks, ...user.packs];
                    }, []);

                    packs.sort((a, b) => a.name.localeCompare(b.name));

                    this.currentPacks.set(packs);
                }
            },
            error: () => {}
        });
    }


    /**
     * Loads the user's packs from the server.
     */
    public loadUserPacks(force: boolean = false) {
        if(this.userPacks().length > 0 && !force) {
            return;
        }
        this.http.post(this.app.getApiEndpoint() + '/oauth/get-user-packs', {}).subscribe({
            next: (response: ApiResponse) => {
                if(response.results) {
                    this.storeCheckHash = JSON.stringify(response.results);
                    this.userPacks.set(response.results);
                }
            },
            error: () => {}
        });
    }


    /**
     * Retrieves a pack by its ID.
     * @param id - The ID of the pack to retrieve.
     * @returns The pack with the specified ID, or undefined if not found.
     */
    public getPackById(id: string) {
        return this.currentPacks().find(p => p.id === id);
    }


    /**
     * Searches for packs that contain the specified text in their name, owner, or tickets.
     * @param searchText - The text to search for (case-insensitive).
     * @returns An array of packs that match the search criteria.
     */
    public searchPacks(searchText: string): Pack[] {
        if (!searchText || searchText.trim().length === 0) {
            return this.currentPacks();
        }

        const searchTerm = searchText.toLowerCase().trim();
        
        return this.currentPacks().filter(pack => {
            // Search in pack name
            const nameMatch = pack.name?.toLowerCase().includes(searchTerm);
            
            // Search in pack owner name
            const ownerMatch = pack.owner_name?.toLowerCase().includes(searchTerm);
            
            // Search in pack origin
            const originMatch = pack.origin?.toLowerCase().includes(searchTerm);
            
            // Search in individual ticket names/types if they exist
            const ticketMatch = pack.tickets?.some(ticket => 
                ticket.name?.toLowerCase().includes(searchTerm) ||
                ticket.ticket_type?.toLowerCase().includes(searchTerm)
            );

            return nameMatch || ownerMatch || originMatch || ticketMatch;
        });
    }


    /**
     * Adds a new pack to the current packs.
     * @param pack - The pack to add.
     * @returns True if the pack was added, false if it has no tickets.
     */
    public addPack(pack: Pack) {
        if(pack.tickets.length === 0) {
            return false;
        }

        this.userPacks.set([...this.userPacks(), pack]);
        this.currentPacks.set([...this.currentPacks(), pack]);

        return true;
    }


    /**
     * Removes a pack from the current packs by its ID.
     * @param id - The ID of the pack to remove.
     */
    public removePack(id: string) {
        this.userPacks.set(this.userPacks().filter(p => p.id !== id));
        this.currentPacks.set(this.currentPacks().filter(p => p.id !== id));
    }


    /**
     * Validates if the given seat options can accommodate the tickets in the specified pack.
     * 
     * @param {string} packId - The ID of the pack to validate.
     * @param {Seat[]} [seatOptions=[]] - An array of seat options to validate against.
     * @returns {boolean | PackSeatValidation[]} - Returns true if the seat options are valid, 
     * or an array of PackSeatValidation objects indicating the differences if not.
     */
    public validatePackOnSeatOptions(packId: string, seatOptions: Seat[] = []): boolean | PackSeatValidation[] {
        const pack = this.getPackById(packId);

        if (!pack || !seatOptions || seatOptions.length === 0) {
            return false;
        }

        const ticketsByType = pack.tickets.reduce((acc, ticket) => {
            acc[ticket.ticket_type] = (acc[ticket.ticket_type] || 0) + 1;
            return acc;
        }, {} as PackSeatValidation);

        const seatByType = seatOptions.reduce((acc, seat) => {
            acc[seat.slug] = seat.available_seats;
            return acc;
        }, {} as PackSeatValidation);

        const differences = Object.entries(ticketsByType).reduce((acc, [ticketType, ticketCount]) => {
            const availableSeats = seatByType[ticketType] || 0;
            if (ticketCount > availableSeats) {
                acc.push({
                    ticketType,
                    difference: availableSeats - ticketCount
                });
            }
            return acc;
        }, [] as PackSeatValidation[]);

        return differences.length > 0 ? differences : true;
    }


    /**
     * Stores the current packs in the database.
     * @param packs 
     * @returns The packs from the server.
     */
    private storeUserPacks(packs: Pack[]) {
        const url = this.app.getApiEndpoint() + '/oauth/set-user-packs'
        this.http.post(url, { packs: packs }).subscribe({
            next: (response: ApiResponse) => {
                // Reload all packs
                this.storeCheckHash = JSON.stringify(response.results);
                this.loadAllUsersPacks(true);
            },
            error: (error) => {}
        });
    }
}